import {
  type MRT_ColumnDef,
  type MRT_Cell,
  type MRT_RowData,
} from 'material-react-table';

export interface WealthSliderConfig {
  min: number;
  max: number;
  step: number;
  marks?: boolean;
  midpoint?: number;
}

export const ANNUAL_INCOME_CONFIG: WealthSliderConfig = {
  min: 30_000,
  max: 5_000_000,
  step: 10_000,
  marks: true,
  midpoint: 300_000,
};

export const LIQUID_NET_WORTH_CONFIG: WealthSliderConfig = {
  min: 10_000,
  max: 10_000_000,
  step: 25_000,
  marks: true,
  midpoint: 300_000,
};

export function createWealthSliderProps(config: WealthSliderConfig) {
  // Create marks that reflect a logarithmic distribution for better visual balance
  const createMarks = () => {
    if (!config.marks) return false;
    if (!config.midpoint) return true;

    const { min, max, midpoint } = config;

    // Use logarithmic spacing to create more intuitive mark distribution
    const logMin = Math.log(min);
    const logMidpoint = Math.log(midpoint);
    const logMax = Math.log(max);

    // Calculate logarithmically spaced values
    const midValue = Math.exp(logMin + (logMidpoint - logMin) * 0.5);
    const upperMid = Math.exp(logMidpoint + (logMax - logMidpoint) * 0.25);
    const upperHigh = Math.exp(logMidpoint + (logMax - logMidpoint) * 0.6);

    const marks = [
      { value: min },
      {
        value: Math.round(midValue / 1000) * 1000,
      },
      {
        value: midpoint,
      },
      {
        value: Math.round(upperMid / 100000) * 100000,
      },
      {
        value: Math.round(upperHigh / 100000) * 100000,
      },
      { value: max },
    ];

    return marks;
  };

  return {
    marks: createMarks(),
    max: config.max,
    min: config.min,
    step: config.step,
    // Remove the custom scale to fix tooltip values
    // The marks positioning will provide the visual distribution we want
    valueLabelFormat: (value: number) =>
      value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
  };
}

export function createWealthColumnCell<T extends MRT_RowData>() {
  return ({ cell }: { cell: MRT_Cell<T, unknown> }) => {
    const value = cell.getValue() as number | null | undefined;
    return value
      ? value.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })
      : '-';
  };
}

export function createWealthColumnConfig<T extends MRT_RowData>(
  accessorKey: keyof T,
  header: string,
  config: WealthSliderConfig,
  size: number = 150,
  enableEditing: boolean = false,
  enableGrouping: boolean = true
): MRT_ColumnDef<T> {
  return {
    accessorKey: accessorKey as string,
    header,
    Cell: createWealthColumnCell<T>(),
    filterVariant: 'range-slider',
    filterFn: 'betweenInclusive',
    muiFilterSliderProps: createWealthSliderProps(config),
    size,
    enableEditing,
    enableGrouping,
  };
}
